import { api } from "@/api/client";
import { toDisplayUsd } from "@/lib/format";

export type MarketCatalog = {
  networks?: { code: string; name?: string; nameFa?: string; logoUrl?: string }[];
  tokens?: {
    networkCode: string;
    symbol: string;
    name?: string;
    logoUrl?: string;
    chainSymbol?: string;
    decimals?: number;
  }[];
};

export type MarketPriceItem = {
  networkCode: string;
  symbol: string;
  /** Display USD (oracle metals → USD per gram). */
  priceUsd?: string | number;
  usd?: string | number;
  updatedAt?: string;
  unit?: string;
  source?: string;
  metal?: string | null;
};

function normPrice(it: MarketPriceItem & Record<string, unknown>): MarketPriceItem {
  const unit = String(it.unit || "");
  const raw = it.priceUsd ?? it.usd ?? (it as { price?: unknown }).price;
  const display = toDisplayUsd(raw, unit);
  return {
    networkCode: String(it.networkCode || ""),
    symbol: String(it.symbol || ""),
    priceUsd: display != null ? display : (raw as string | number | undefined),
    usd: it.usd,
    updatedAt: it.updatedAt,
    unit: unit === "usd_per_nanogram" ? "usd" : unit || "usd",
    source: it.source || (it as { source?: string }).source,
    metal: (it.metal as string | null | undefined) ?? null,
  };
}

export async function fetchMarketCatalog(): Promise<MarketCatalog> {
  return api<MarketCatalog>("/market/catalog");
}

export async function fetchMarketPrices(): Promise<MarketPriceItem[]> {
  const out = await api<{ items: MarketPriceItem[] }>("/market/prices");
  return (out.items || []).map((it) => normPrice(it as MarketPriceItem & Record<string, unknown>));
}

export async function fetchSpotMeta(): Promise<{
  engineLiveOrderTypes: string[];
  supportedOrderTypes: string[];
  pairsHint?: string[];
  fx?: { usdIrt?: string; stale?: boolean; source?: string };
}> {
  return api("/spot/meta");
}

export async function fetchSpotFx(): Promise<{
  usdIrt?: string;
  stale?: boolean;
  source?: string;
}> {
  return api("/spot/fx");
}

export type SpotMarketRow = {
  pair?: string;
  symbol?: string;
  base?: string;
  quote?: string;
  networkCode?: string;
  tokenSymbol?: string;
  kind?: string;
  status?: string;
};

export async function fetchSpotMarkets(
  token?: string | null
): Promise<{ spot?: SpotMarketRow[]; fx?: unknown; networks?: string[] }> {
  return api("/spot/markets", {}, token || undefined);
}

export async function fetchSpotBook(token: string | null | undefined, pair: string): Promise<SpotBook> {
  return api(`/spot/book?pair=${encodeURIComponent(pair)}`, {}, token || undefined);
}

export async function fetchSpotTicker(
  token: string | null | undefined,
  pair: string
): Promise<Record<string, unknown>> {
  return api(`/spot/ticker?pair=${encodeURIComponent(pair)}`, {}, token || undefined);
}

export type OhlcCandle = {
  time: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume?: string;
};

export type SpotOhlc = {
  ok?: boolean;
  pair?: string;
  interval?: string;
  candles?: OhlcCandle[];
  source?: string;
  emptyReason?: string | null;
  degraded?: boolean;
  gaps?: string[];
};

export async function fetchSpotOhlc(
  token: string | null | undefined,
  pair: string,
  opts?: { interval?: string; limit?: number }
): Promise<SpotOhlc> {
  const interval = opts?.interval || "1m";
  const limit = opts?.limit ?? 120;
  const q = `pair=${encodeURIComponent(pair)}&interval=${encodeURIComponent(interval)}&limit=${limit}`;
  return api(`/spot/ohlc?${q}`, {}, token || undefined);
}

export type SpotBookLevel = { price?: string | number; qty?: string | number; amount?: string | number };
export type SpotBook = {
  bids?: SpotBookLevel[];
  asks?: SpotBookLevel[];
  buy?: SpotBookLevel[];
  sell?: SpotBookLevel[];
  levels?: { bids?: SpotBookLevel[]; asks?: SpotBookLevel[] };
  degraded?: boolean;
};

export type SpotOpenOrder = {
  orderId?: string;
  id?: string;
  public_id?: string;
  side?: string;
  type?: string;
  orderType?: string;
  status?: string;
  amountNag?: string | number;
  limitUsdPerNag?: string | null;
};

export async function fetchSpotOpenOrders(token: string): Promise<SpotOpenOrder[]> {
  const out = await api<{ orders?: SpotOpenOrder[]; items?: SpotOpenOrder[] }>(
    "/spot/orders",
    {},
    token
  );
  return out.orders || out.items || [];
}

export async function cancelSpotOrder(token: string, orderId: string): Promise<unknown> {
  return api(`/spot/orders/${encodeURIComponent(orderId)}`, { method: "DELETE" }, token);
}

export async function placeSpotOrder(
  token: string,
  body: {
    side: string;
    orderType: string;
    amountNag: number;
    limitUsdPerNag?: string;
    tokenSymbol?: string;
    pair?: string;
    triggerPrice?: string;
  }
): Promise<unknown> {
  return api("/spot/orders", { method: "POST", body: JSON.stringify(body) }, token);
}

export async function fetchInbox(token: string): Promise<unknown[]> {
  const out = await api<{ items: unknown[] }>("/inbox", {}, token);
  return out.items || [];
}

export async function startIrrDeposit(token: string): Promise<{ depositUrl?: string }> {
  return api("/irr/deposit/start", { method: "POST", body: "{}" }, token);
}

export async function fetchPublicSettings(): Promise<{ siteUrl?: string; site_url?: string }> {
  return api("/settings/public");
}
